import React, { useEffect } from 'react';
import { useApplicationStore } from '../../stores/applicationStore';
import { useAuthStore } from '../../stores/authStore';
import { 
  Building2, 
  Clock, 
  CheckCircle, 
  XCircle, 
  AlertCircle, 
  Plus,
  Calendar,
  FileText,
  ArrowRight,
  Users,
  Target,
  Award,
  Palette
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';

const COLORS = ['#fbbf24', '#3b82f6', '#10b981', '#ef4444', '#6b7280'];

// Mappa colori per stato specifico
const getStatusColor = (status: string) => {
  switch (status) {
    case 'In attesa':
      return '#fbbf24'; // giallo
    case 'Colloquio programmato':
      return '#3b82f6'; // blu
    case 'Accettato':
      return '#10b981'; // verde
    case 'Rifiutato':
      return '#ef4444'; // rosso
    case 'Scaduto':
      return '#6b7280'; // grigio
    default:
      return '#6b7280'; // grigio di default
  }
};

const statusBadge = {
  'In attesa': { color: 'bg-yellow-100 text-yellow-800 border-yellow-200', icon: <Clock className="w-3 h-3" /> },
  'Colloquio programmato': { color: 'bg-blue-100 text-blue-800 border-blue-200', icon: <Calendar className="w-3 h-3" /> },
  'Accettato': { color: 'bg-green-100 text-green-800 border-green-200', icon: <CheckCircle className="w-3 h-3" /> },
  'Rifiutato': { color: 'bg-red-100 text-red-800 border-red-200', icon: <XCircle className="w-3 h-3" /> },
  'Scaduto': { color: 'bg-gray-100 text-gray-600 border-gray-200', icon: <AlertCircle className="w-3 h-3" /> },
};

// Custom label for PieChart: mostra solo le percentuali sulle fette
const renderPieLabel = (props: any) => {
  const { percent, cx, cy, midAngle, innerRadius, outerRadius } = props;
  if (!percent || percent === 0) return null;
  
  const RADIAN = Math.PI / 180;
  // Posiziona le percentuali al centro di ogni fetta
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  
  return (
    <text
      x={x}
      y={y}
      fill="white"
      textAnchor="middle"
      dominantBaseline="central"
      fontSize={11}
      fontWeight={600}
    >
      {(percent * 100).toFixed(0)}%
    </text>
  );
};

export const Dashboard: React.FC = () => {
  const { 
    stats, 
    loading, 
    initialized,
    loadApplications,
    applications
  } = useApplicationStore();
  const { user, userProfile } = useAuthStore();
  const navigate = useNavigate();

  // Carica le candidature quando l'utente è autenticato
  useEffect(() => {
    if (user?.uid && !initialized && !loading) {
      console.log('🔄 Caricamento candidature per dashboard:', user.uid);
      loadApplications(user.uid);
    }
  }, [user?.uid, initialized, loading, loadApplications]);

  if (loading && !initialized) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Caricamento dashboard...</p>
        </div>
      </div>
    );
  }

  // Prepara i dati per il grafico a torta
  const pieData = stats?.statusCounts ? Object.entries(stats.statusCounts).map(([status, count]) => ({
    name: status,
    value: count
  })) : [];

  // Estrai tutti i colloqui da tutte le candidature
  const allInterviews = applications.flatMap((app: any) =>
    (app.interviews || []).map((intv: any) => ({
      ...intv,
      companyName: app.companyName,
      position: app.position,
      applicationId: app.id,
      appliedDate: app.appliedDate,
    }))
  );

  // Ordina i colloqui per data (più recenti prima) e prendi i primi 5
  const recentInterviews = allInterviews
    .sort((a: any, b: any) => b.date.seconds - a.date.seconds)
    .slice(0, 2); // Prendi solo 2 colloqui recenti

  // Prendi le prime 5 applicazioni recenti
  const recentApplications = applications.slice(0, 5);
  
  // Combina le applicazioni recenti con le altre attività
  const recentActivity = [
    ...recentApplications.map((app: any) => ({
      id: app.id,
      type: 'application',
      title: `Candidatura per ${app.companyName}`,
      description: app.position,
      date: app.appliedDate?.toDate() || new Date(),
      status: app.status
    })),
    // Aggiungi altre attività se necessario
  ].sort((a, b) => b.date.getTime() - a.date.getTime()).slice(0, 10);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-2 lg:p-4 lg:pr-8 space-y-4 lg:space-y-6">
      {/* Header moderno */}
      <div className="bg-white rounded-xl lg:rounded-2xl shadow-lg p-4 lg:p-6 mb-2">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 lg:gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 lg:gap-3 mb-2">
              <div className="p-1.5 lg:p-2 bg-blue-100 rounded-lg lg:rounded-xl">
                <Users className="w-5 h-5 lg:w-6 lg:h-6 text-blue-600" />
              </div>
              <div>
                <h1 className="text-xl lg:text-2xl xl:text-3xl font-bold text-gray-900">Dashboard Utente</h1>
                <p className="text-sm lg:text-base text-gray-600">Benvenuto, {userProfile?.name || 'Utente'}! 👋</p>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 lg:gap-3">
            <button
              onClick={() => navigate('/applications/new')}
              className="flex items-center px-3 py-2 lg:px-4 lg:py-2 bg-blue-600 text-white rounded-lg lg:rounded-xl hover:bg-blue-700 transition-colors text-sm lg:text-base font-medium"
            >
              <Plus className="h-4 w-4 lg:h-5 lg:w-5 mr-2" />
              Nuova Candidatura
            </button>
          </div>
        </div>
      </div>

      {/* Statistiche Principali - Mobile: 2x2, Desktop: 1x4 */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
        <div className="bg-white rounded-xl lg:rounded-2xl shadow-lg p-4 lg:p-6 hover:shadow-xl transition-shadow cursor-pointer" onClick={() => navigate('/applications')}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs lg:text-sm font-medium text-gray-600 mb-1">Totale</p>
              <p className="text-2xl lg:text-3xl font-bold text-gray-900">{stats?.totalApplications || 0}</p>
              <p className="text-xs text-gray-500 mt-1">Candidature</p>
            </div>
            <div className="p-2 lg:p-3 bg-blue-100 rounded-lg lg:rounded-xl">
              <Building2 className="w-5 h-5 lg:w-6 lg:h-6 text-blue-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl lg:rounded-2xl shadow-lg p-4 lg:p-6 hover:shadow-xl transition-shadow cursor-pointer" onClick={() => navigate('/applications?status=In attesa')}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs lg:text-sm font-medium text-gray-600 mb-1">In Attesa</p>
              <p className="text-2xl lg:text-3xl font-bold text-gray-900">{stats?.statusCounts?.['In attesa'] || 0}</p>
              <p className="text-xs text-gray-500 mt-1">Candidature</p>
            </div>
            <div className="p-2 lg:p-3 bg-yellow-100 rounded-lg lg:rounded-xl">
              <Clock className="w-5 h-5 lg:w-6 lg:h-6 text-yellow-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl lg:rounded-2xl shadow-lg p-4 lg:p-6 hover:shadow-xl transition-shadow cursor-pointer" onClick={() => navigate('/applications?status=Colloquio programmato')}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs lg:text-sm font-medium text-gray-600 mb-1">Colloqui</p>
              <p className="text-2xl lg:text-3xl font-bold text-gray-900">{stats?.statusCounts?.['Colloquio programmato'] || 0}</p>
              <p className="text-xs text-gray-500 mt-1">Programmato</p>
            </div>
            <div className="p-2 lg:p-3 bg-blue-100 rounded-lg lg:rounded-xl">
              <Calendar className="w-5 h-5 lg:w-6 lg:h-6 text-blue-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl lg:rounded-2xl shadow-lg p-4 lg:p-6 hover:shadow-xl transition-shadow cursor-pointer" onClick={() => navigate('/applications?status=Accettato')}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs lg:text-sm font-medium text-gray-600 mb-1">Accettate</p>
              <p className="text-2xl lg:text-3xl font-bold text-gray-900">{stats?.statusCounts?.['Accettato'] || 0}</p>
              <p className="text-xs text-gray-500 mt-1">Candidature</p>
            </div>
            <div className="p-2 lg:p-3 bg-green-100 rounded-lg lg:rounded-xl">
              <CheckCircle className="w-5 h-5 lg:w-6 lg:h-6 text-green-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Layout Desktop Enhanced - Due colonne su desktop */}
      <div className="lg:grid lg:grid-cols-2 lg:gap-8 lg:mb-8">
        {/* Grafico a Torta - Desktop Enhanced */}
        <div className="mb-6 lg:mb-0">
          <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 lg:p-8">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg lg:text-xl font-semibold text-gray-900">Distribuzione Stati</h3>
              <Target className="h-5 w-5 lg:h-6 lg:w-6 text-gray-400" />
            </div>
            {pieData.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={renderPieLabel}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={getStatusColor(entry.name)} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                <div className="mt-6 flex flex-wrap justify-center gap-4">
                  {pieData.map((entry, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded-full" style={{ backgroundColor: getStatusColor(entry.name) }} />
                      <span className="text-sm text-gray-600">{entry.name} ({entry.value})</span>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="flex items-center justify-center h-40 text-gray-500">
                <div className="text-center">
                  <FileText className="h-10 w-10 mx-auto mb-2" />
                  <p className="text-sm">Nessuna candidatura</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Attività Recente - Desktop Enhanced */}
        <div className="lg:mb-0">
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="flex items-center justify-between p-4 sm:p-6 lg:p-8 border-b border-gray-100">
              <h3 className="text-lg lg:text-xl font-semibold text-gray-900">Attività Recente</h3>
              <button
                onClick={() => navigate('/applications')}
                className="flex items-center gap-1 text-blue-600 hover:text-blue-700 text-sm lg:text-base font-medium"
              >
                Vedi tutte
                <ArrowRight className="h-4 w-4 lg:h-5 lg:w-5" />
              </button>
            </div>
            {recentActivity && recentActivity.length > 0 ? (
              <div className="divide-y divide-gray-100">
                {recentActivity.map((item) => (
                  <div
                    key={`${item.type}-${item.id}`}
                    className="p-4 sm:p-6 lg:p-8 hover:bg-gray-50 transition-colors cursor-pointer"
                    onClick={() => {
                      if (item.type === 'application') {
                        navigate(`/applications/${item.id}`);
                      } else {
                        navigate(`/calendar`);
                      }
                    }}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-3">
                          {item.type === 'interview' ? (
                            <Calendar className="h-5 w-5 text-green-500" />
                          ) : (
                            <Building2 className="h-5 w-5 text-blue-500" />
                          )}
                          <h4 className="font-semibold text-gray-900 truncate text-base">
                            {item.title}
                          </h4>
                        </div>
                        {item.type === 'application' && (
                          <div className="mb-3">
                            <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border ${statusBadge[(item as any).status as keyof typeof statusBadge]?.color || 'bg-gray-100 text-gray-800 border-gray-200'}`}>
                              {statusBadge[(item as any).status as keyof typeof statusBadge]?.icon}
                              <span className="hidden sm:inline">{(item as any).status}</span>
                              <span className="sm:hidden">{(item as any).status === 'In attesa' ? 'In Attesa' : (item as any).status === 'Colloquio programmato' ? 'Colloquio' : (item as any).status}</span>
                            </span>
                          </div>
                        )}
                        <p className="text-sm text-gray-600 mb-3 font-medium">
                          {item.description}
                        </p>
                        <div className="flex items-center gap-3 text-sm text-gray-500">
                          <span>
                            {format(item.date, 'dd/MM/yyyy', { locale: it })}
                          </span>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        {item.type === 'interview' && (
                          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium bg-green-100 text-green-800 border border-green-200">
                            <Calendar className="w-4 h-4" />
                            <span className="hidden sm:inline">Colloquio</span>
                            <span className="sm:hidden">Colloquio</span>
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 lg:py-12 px-4">
                <Building2 className="h-16 w-16 lg:h-20 lg:w-20 text-gray-300 mx-auto mb-4" />
                <h4 className="text-lg lg:text-xl font-semibold text-gray-900 mb-2">
                  Nessuna candidatura recente
                </h4>
                <p className="text-gray-600 mb-6 text-sm lg:text-base">
                  Inizia aggiungendo la tua prima candidatura
                </p>
                <button
                  onClick={() => navigate('/applications/new')}
                  className="inline-flex items-center gap-2 px-6 py-3 lg:px-8 lg:py-4 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors text-sm lg:text-base font-medium"
                >
                  <Plus className="h-4 w-4 lg:h-5 lg:w-5" />
                  <span>Prima Candidatura</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Quick Actions - Mobile-First */}
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <button
          onClick={() => navigate('/applications')}
          className="bg-white rounded-2xl shadow-lg p-4 hover:shadow-xl transition-all duration-200 text-left"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-xl">
              <FileText className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h4 className="font-semibold text-gray-900">Gestisci Candidature</h4>
              <p className="text-sm text-gray-600">Visualizza e modifica</p>
            </div>
          </div>
        </button>
        <button
          onClick={() => navigate('/calendar')}
          className="bg-white rounded-2xl shadow-lg p-4 hover:shadow-xl transition-all duration-200 text-left"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-xl">
              <Calendar className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <h4 className="font-semibold text-gray-900">Calendario</h4>
              <p className="text-sm text-gray-600">Pianifica colloqui</p>
            </div>
          </div>
        </button>
      </div>
    </div>
  );
}; 